import type { ScreenSchema } from './types';

/**
 * Schema for the `assessment` template (uc-assessment.vue).
 *
 * The assessment is a fully self-contained slides-based quiz: intro slide →
 * question slides → result slide. It creates its own ScrollTrigger in onMounted
 * and manages ALL audio imperatively, so `managesOwnSectionAudio: true` is
 * required to suppress uc-screen's generic intro watcher.
 *
 * Audio slots:
 *   - `intro`              — Optional `title` (parent screen) plus either plain
 *                           `assessment.introduction` (string) **or** all
 *                           `assessment.introduction.templates[]` mini-screens
 *                           (heading → text → instructions each), same as
 *                           default content blocks.
 *                           S3 key: `{screen.id}-introduction`
 *   - `results-passed`     — text shown after the learner passes.
 *                           Sourced from `assessment.results.passed` (YAML) or
 *                           the component-computed default via overrideText.
 *                           S3 key: `{screen.id}-results-passed`
 *   - `results-failed`     — text shown after the learner fails.
 *                           S3 key: `{screen.id}-results-failed`
 *   - `results-max-attempts` — text shown when attempts are exhausted.
 *                           S3 key: `{screen.id}-results-max-attempts`
 *
 * Question audio is handled directly inside uc-assessment.vue's
 * `playQuestionAudio()` using the tier system:
 *   tier 1 — `question.audio ?? question.speech`
 *   tier 2 — JiT via `resolveChildIntro(question)` (useChildScreenAudio)
 *   tier 3 — S3 via `getAudioURL(question.id)`
 *
 * Implementation note: uc-assessment.vue uses a synthetic screen object for
 * useScreenAudio: `{ id, title?, template: 'assessment', assessment, 'placeholder-values' }`.
 * `placeholder-values` mirrors `uc-text` `:values` (`total`, `required`, `totalEnglish`,
 * `requiredEnglish`, etc.) so JiT `fillTemplates` can resolve `${…}` placeholders in intro
 * and result override strings.
 */
const assessmentSchema: ScreenSchema = {
  template: 'assessment',
  managesOwnSectionAudio: true,
  slots: {
    intro: {
      parts: [
        { field: 'title' },
        {
          iterateField: 'assessment.introduction.templates',
          parts: [
            { field: 'heading', fallback: ['subheading', 'title'] },
            { field: 'text' },
            { field: 'instructions' }
          ]
        },
        { field: 'assessment.introduction' },
        { field: 'assessment.instructions' }
      ]
    }
  }
};

export default assessmentSchema;
